import { useEffect, useRef, useState, useCallback } from "react";

interface UseAutoSaveOptions {
  delay?: number;
  onSave: (value: any) => Promise<void>;
  onError?: (error: any) => void;
}

export const useAutoSave = <T>(
  initialValue: T,
  options: UseAutoSaveOptions
) => {
  const { delay = 1000, onSave, onError } = options;
  const [value, setValue] = useState<T>(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedValueRef = useRef<T>(initialValue);

  const save = useCallback(
    async (valueToSave: T) => {
      if (
        JSON.stringify(valueToSave) ===
        JSON.stringify(lastSavedValueRef.current)
      ) {
        return; // No changes to save
      }

      setIsSaving(true);
      try {
        await onSave(valueToSave);
        lastSavedValueRef.current = valueToSave;
        setHasUnsavedChanges(false);
      } catch (error) {
        onError?.(error);
      } finally {
        setIsSaving(false);
      }
    },
    [onSave, onError]
  );

  const debouncedSave = useCallback(
    (newValue: T) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setHasUnsavedChanges(true);
      timeoutRef.current = setTimeout(() => {
        save(newValue);
      }, delay);
    },
    [save, delay]
  );

  const updateValue = useCallback(
    (newValue: T) => {
      setValue(newValue);
      debouncedSave(newValue);
    },
    [debouncedSave]
  );

  const handleBlur = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (hasUnsavedChanges) {
      save(value);
    }
  }, [save, value, hasUnsavedChanges]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    value,
    updateValue,
    handleBlur,
    isSaving,
    hasUnsavedChanges,
  };
};
